import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** A full-screen overlay held open by a search param on the current URL, `value` being what opening writes. */
export type UrlOverlay = { param: string; value: string; exactValue: boolean };

/** The overlay's param is on the URL, whatever its value; a screen that remembers the URL drops it. */
export const hasUrlOverlay = (params: URLSearchParams, { param }: UrlOverlay) => params.has(param);

/** The overlay is open: its param is present, holding exactly its value when it requires one. */
const opensUrlOverlay = (params: URLSearchParams, overlay: UrlOverlay) =>
  overlay.exactValue ? params.get(overlay.param) === overlay.value : hasUrlOverlay(params, overlay);

/** The search that opens `overlay`, such as `?checkout=success`, for a URL built outside the router. */
export const urlOverlaySearch = ({ param, value }: UrlOverlay) => `?${new URLSearchParams({ [param]: value })}`;

/**
 * A full-screen overlay opened by a search param on the current URL rather than a route: the screen under it
 * stays mounted, a reload reopens it, and a Stripe return URL can open it. Opening and closing replace the
 * history entry, so Back and Forward never toggle it. One of `overlays` is open at a time: with several on the
 * URL, the first listed wins.
 */
export const useUrlOverlay = ({ param, value }: UrlOverlay, overlays: Readonly<Record<string, UrlOverlay>>) => {
  const { pathname, search, hash, state } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const openOverlay = Object.values(overlays).find((overlay) => opensUrlOverlay(params, overlay));

  const replaceParams = useCallback(
    (update: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(search);
      update(params);
      const nextSearch = params.toString();
      void navigate({ pathname, search: nextSearch && `?${nextSearch}`, hash }, { replace: true, state });
    },
    [pathname, search, hash, state, navigate]
  );

  return {
    isOpen: openOverlay?.param === param,
    open: useCallback(() => replaceParams((params) => params.set(param, value)), [replaceParams, param, value]),
    close: useCallback(() => replaceParams((params) => params.delete(param)), [replaceParams, param]),
    /** Closes this overlay and opens `next` in one navigation, as two would each start from the same URL. */
    switchTo: useCallback(
      (next: UrlOverlay) =>
        replaceParams((params) => {
          params.delete(param);
          params.set(next.param, next.value);
        }),
      [replaceParams, param]
    ),
  };
};
